import { useEffect, useState } from "react";
import { useContracts } from "./useHook.";
import { EyeIcon, TrashIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

function Contracts() {
  const navigate = useNavigate();
  const { createContract, getContracts, deleteContract } = useContracts();

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const res = await getContracts();
      setContracts(res || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, []);

  const handleUpload = async () => {
    if (!file || !title) return;

    const formData = new FormData();
    formData.append("title", title);
    formData.append("file", file);

    try {
      setUploading(true);
      await createContract(formData);

      setFile(null);
      setTitle("");
      setIsModalOpen(false);
      fetchContracts();

      Swal.fire("Success", "Contract uploaded successfully", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Upload failed", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this contract!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteContract(id);
        fetchContracts();

        Swal.fire("Deleted!", "Contract has been deleted.", "success");
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Delete failed", "error");
      }
    }
  };

  const handleView = (id) => {
    navigate(`/contracts/view/${id}`);
  };

  return (
    <div className="p-6 min-h-screen">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold mb-8 text-gray-800">
          Contract Management
        </h2>

        {/* Actions */}
        <div className="text-center mb-6 flex gap-3">
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-blue-500 text-white px-6 py-3 rounded-lg shadow hover:bg-blue-600 transition duration-200"
          >
            + Add Contract
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <p className="text-center">Loading...</p>
      ) : contracts.length === 0 ? (
        <p className="text-center text-gray-500">No contracts found</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {contracts.map((contract) => (
            <div
              key={contract.id}
              className="bg-white p-5 rounded-2xl shadow-md hover:shadow-lg transition duration-200 border border-gray-100"
            >
              {/* Title */}
              Title <h3 className="font-semibold text-lg text-gray-800 mb-4 truncate">
                {contract.title}
              </h3>

              {/* Buttons */}
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => handleView(contract.id)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-blue-700 focus:bg-blue-200 focus:text-blue-800 focus:font-semibold focus:shadow-lg transition duration-200"
                >
                  <EyeIcon className="h-5 w-5" />
                  View
                </button>

                <button
                  onClick={() =>
                    navigate(`/contracts/user-contracts/${contract.id}`)
                  }
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-blue-700 focus:bg-blue-200 focus:text-blue-800 focus:font-semibold focus:shadow-lg transition duration-200"
                >
                  <DocumentTextIcon className="h-5 w-5" />
                  User PDFs
                </button>

                <button
                  onClick={() => handleDelete(contract.id)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-600 focus:bg-red-100 focus:text-red-700 focus:font-semibold focus:shadow-lg transition duration-200"
                >
                  <TrashIcon className="h-5 w-5" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
          <div className="bg-white w-[420px] p-6 rounded-2xl shadow-xl relative">

            {/* Title */}
            <h3 className="text-2xl font-semibold text-gray-800 mb-5 text-center">
              Upload Contract
            </h3>

            {/* Title Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Contract Title
              </label>
              <input
                type="text"
                placeholder="Enter contract title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
            </div>

            {/* File Upload */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Upload File
              </label>

              <label
                htmlFor="file-upload"
                className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 transition"
              >
                <svg
                  className="w-10 h-10 text-gray-400 mb-2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 16V4m0 0l-4 4m4-4l4 4M4 20h16" />
                </svg>

                <p className="text-gray-600 text-sm">
                  {file ? file.name : "Click or drag Word file here"}
                </p>

                <span className="text-xs text-gray-400 mt-1">
                  Only .doc / .docx allowed
                </span>

                <input
                  id="file-upload"
                  type="file"
                  accept=".doc,.docx"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="hidden"
                />
              </label>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="w-full py-2.5 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
              >
                Cancel
              </button>

              <button
                onClick={handleUpload}
                disabled={uploading || !file || !title}
                className="w-full py-2.5 rounded-lg bg-green-500 text-white hover:bg-green-600 transition disabled:opacity-50 flex justify-center items-center"
              >
                {uploading ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="white"
                        strokeWidth="3"
                        fill="none"
                      />
                    </svg>
                    Uploading...
                  </span>
                ) : (
                  "Upload"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Contracts;